import { Injectable, OnDestroy } from '@angular/core';
import { HighchartsThemeService } from '@frk/eds-components';
import { SiteConfigService } from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import { ChartConfig, LegendSeries } from '@types';
import { Logger } from '@utils/logger';
import groupBy from 'lodash/groupBy';
import { BehaviorSubject, Observable, forkJoin, Subject } from 'rxjs';
import { map, take, catchError, takeUntil } from 'rxjs/operators';
import USIndexAssetAllocationsQuery from '../../graphql/us-index-asset-allocations.graphql';
import { IndexesService } from '../../services/indexes.service';
import { IndexAssetAllocation, Indexes } from '../../types/model/indexes';
import {
  DownloadXlsxLabel,
  USIndexAllocationsBRConfig,
} from '../../types/us-index.type';
import {
  IndexesAllocation,
  INDEXES_ALLOCATIONS_DATE_RANGES,
  INDEXES_ALLOCATIONS_ORDER,
} from '../us-index-allocations.config';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { StickyTab } from '@frk/eds-components';

const logger = Logger.getLogger('UsIndexAllocationsService');

type CashType = 'POSITIVE' | 'NEGATIVE';

@Injectable({
  providedIn: 'root',
})
export class UsIndexAllocationsService implements OnDestroy {
  private indexes$: Observable<Indexes>;
  private brConfig: USIndexAllocationsBRConfig;
  private currentRange$: BehaviorSubject<string> = new BehaviorSubject<string>(
    'ONE_MONTH'
  );
  private userRangeChange$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );
  private bottomCaveat: string[][] = [];
  private topCaveat: string[][] = [];
  private ticker: string;
  private asOfDate: string;
  private categories: IndexAssetAllocation[][];
  private cellData: string[][];
  private fundName: string;
  private labels?: DownloadXlsxLabel;
  private fundTabs: StickyTab[] = [];
  private showAllocations$: BehaviorSubject<boolean> = new BehaviorSubject(
    false
  );

  private unsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    private indexesService: IndexesService,
    private translateService: TranslateService,
    private highchartsTheme: HighchartsThemeService,
    private siteConfigService: SiteConfigService,
    private caveatService: CaveatService
  ) {}

  /**
   * Fetches indexes data and assign state.
   * @param brConfig bloomreach configuration for us-index
   */
  public populate(brConfig: USIndexAllocationsBRConfig): void {
    this.brConfig = brConfig;
    this.fundName = brConfig?.fundName;
    this.labels = brConfig?.labels;
    this.indexes$ = this.indexesService.register(USIndexAssetAllocationsQuery, {
      fundId: brConfig?.fundId,
      shareClassCode: brConfig?.shareClassCode,
    });
    this.fundTabs = this.brConfig?.tabs.filter((tab) => ({
      id: tab.id,
      title: tab.label,
    }));
    if (
      !!this.fundTabs.find(
        (tab: StickyTab): boolean => tab.id === 'allocations'
      )
    ) {
      this.showAllocations$.next(true);
    }
  }

  public getShowAllocations$(): Observable<boolean> {
    return this.showAllocations$.asObservable();
  }
  public getUserRangeChange$(): Observable<boolean> {
    return this.userRangeChange$.asObservable();
  }

  public setDateRange(rangeId: string): void {
    logger.debug('setDateRange()', rangeId);
    this.currentRange$.next(rangeId);
  }

  public getChartConfig$ = (): Observable<ChartConfig> =>
    this.indexes$.pipe(map(this.mapChartConfig));

  private groupSeries = (obj: IndexAssetAllocation) => obj.categoryNameStd;

  private sortDataPoint = (
    a: Highcharts.PointOptionsObject,
    b: Highcharts.PointOptionsObject
  ): number => a.x - b.x;

  // There should only be 4 possible Indexes allocations series, so we sort in specific order
  private sortSeries = (
    a: Highcharts.SeriesAreaOptions,
    b: Highcharts.SeriesAreaOptions
  ): number => {
    if (this.brConfig.allocationsSeriesOrder) {
      return (
        this.brConfig.allocationsSeriesOrder.indexOf(a.custom.categoryNameStd) -
        this.brConfig.allocationsSeriesOrder.indexOf(b.custom.categoryNameStd)
      );
    }
    return (
      INDEXES_ALLOCATIONS_ORDER.indexOf(a.custom.categoryNameStd) -
      INDEXES_ALLOCATIONS_ORDER.indexOf(b.custom.categoryNameStd)
    );
  };
  // This sorts categories by specific order
  private sortCategories = (
    a: IndexAssetAllocation[],
    b: IndexAssetAllocation[]
  ): number => {
    const dataBrConfig = this.brConfig.allocationsSeriesOrder;
    if (dataBrConfig) {
      return (
        dataBrConfig.indexOf(a[0].categoryNameStd) -
        dataBrConfig.indexOf(b[0].categoryNameStd)
      );
    }
    return (
      INDEXES_ALLOCATIONS_ORDER.indexOf(a[0].categoryNameStd) -
      INDEXES_ALLOCATIONS_ORDER.indexOf(b[0].categoryNameStd)
    );
  };

  // this wil only be used if the legend has a specific order set in the config
  private sortLegendSeries = (a: LegendSeries, b: LegendSeries): number =>
    this.brConfig.allocationsLegendOrder.indexOf(a.categoryNameStd) -
    this.brConfig.allocationsLegendOrder.indexOf(b.categoryNameStd);

  // fn to apply specific colors to specific allocation types
  private getSeriesColor = (
    categoryNameStd: IndexesAllocation,
    index: number
  ): string => {
    logger.debug('getSeriesColor', categoryNameStd, index);
    switch (categoryNameStd) {
      case IndexesAllocation.US_LARGE_CAP_EQUITY:
      case IndexesAllocation.GLOBAL_EQUITY:
      case IndexesAllocation.HIGH_DIVIDEND:
        // #3769ff FT SkyBlue
        return this.highchartsTheme.getPointColor(0);
      case IndexesAllocation.US_TREASURY_7_10_YEAR:
      case IndexesAllocation.US_TREASURY_10_YEAR:
      case IndexesAllocation.LOW_VOLATILITY:
      case IndexesAllocation.US_TREASURY_10_YEAR_FUTURES:
        // #91B9FF FT SkyBlue2
        return this.highchartsTheme.getPointColor(6);
      case IndexesAllocation.US_TREASURY_5_YEAR:
      case IndexesAllocation.US_TREASURY_5_YEAR_FUTURES:
      case IndexesAllocation.GLOBAL_FIXED_INCOME:
        // #DDEAFF FT SkyBlue1
        return '#ddeaff';
      case IndexesAllocation.ALTERNATIVES:
        // #8C8C8C FT Grey3
        return this.highchartsTheme.getPointColor(15);
      case IndexesAllocation.CASH:
      case IndexesAllocation.CASH_NEGATIVE:
        // #BFBFBF FT Grey2
        return this.highchartsTheme.getPointColor(4);
      default:
        // this shouldn't ever happen unless a new allocation type is added
        return this.highchartsTheme.getPointColor(index);
    }
  };

  private mapSeries = (
    points: IndexAssetAllocation[],
    index: number,
    cashType?: CashType
  ): Highcharts.SeriesAreaOptions => ({
    color: this.getSeriesColor(points[0].categoryNameStd, index),
    custom: {
      categoryNameStd:
        cashType === 'NEGATIVE'
          ? IndexesAllocation.CASH_NEGATIVE
          : points[0].categoryNameStd,
    },
    data: points.map(this.mapSeriesData(cashType)).sort(this.sortDataPoint),
    name:
      cashType === 'NEGATIVE'
        ? IndexesAllocation.CASH_NEGATIVE
        : points[0].categoryDescription,
    showInNavigator: true,
    // NB this is key to getting the "mirror" effect when negative cash values
    stacking: cashType === 'NEGATIVE' ? undefined : 'normal',
    turboThreshold: 0,
    type: 'area',
  });

  private reduceSeries = (
    series: Highcharts.SeriesAreaOptions[],
    points: IndexAssetAllocation[],
    index: number
  ): Highcharts.SeriesAreaOptions[] => {
    if (points[0].categoryNameStd === IndexesAllocation.CASH) {
      // for cash we have two series
      // first series is for positive values and is stacked as normal
      // second series for negative values, and NOT stacked
      series.push(this.mapSeries(points, index, 'POSITIVE'));
      series.push(this.mapSeries(points, index, 'NEGATIVE'));
    } else {
      series.push(this.mapSeries(points, index));
    }
    return series;
  };

  private mapSeriesData = (cashType: CashType) => (
    allocation: IndexAssetAllocation
  ): Highcharts.PointOptionsObject => {
    let breakdownPercentageStd: number = allocation.breakdownPercentageStd;
    if (cashType) {
      const isNegativeCashVal = breakdownPercentageStd < 0;
      if (cashType === 'POSITIVE' && isNegativeCashVal) {
        // main Cash series doesn't have negative values
        breakdownPercentageStd = 0;
      }
      if (cashType === 'NEGATIVE' && !isNegativeCashVal) {
        // this Cash series only contains negative values
        breakdownPercentageStd = 0;
      }
    }
    return {
      custom: {
        breakdownPercentage: allocation.breakdownPercentage + '%',
      },
      name: allocation.asOfDate,
      x: Date.parse(allocation.asOfDateStd),
      y: breakdownPercentageStd,
    };
  };

  private mapChartOptions = (
    series: Highcharts.SeriesAreaOptions[]
  ): Highcharts.Options => {
    const millisecondInYear = 31536000000; // Used for setting tickintervals WDE-1457
    const serviceScope = this;
    const opts: Highcharts.Options = {
      chart: {
        type: 'area',
        spacingBottom: 20,
      },
      legend: {
        enabled: false,
      },
      navigator: {
        margin: 32,
        height: 60,
        maskFill: 'rgba(180, 198, 220, 0.15)',
        xAxis: {
          tickLength: 5,
          tickInterval: millisecondInYear,
          labels: {
            align: 'center',
            y: 15,
          },
          dateTimeLabelFormats: {
            day: '%b %d',
            week: '%b %d',
            month: "%b '%y",
          },
        },
      },
      scrollbar: {
        enabled: false,
      },
      plotOptions: {
        area: {
          fillOpacity: 1,
          marker: {
            enabled: false,
          },
        },
      },
      rangeSelector: { enabled: false },
      series,
      title: {
        text: null,
      },
      tooltip: {
        formatter() {
          // filter out the negative cash series from tooltips
          return `
          <strong>${this.points[0].point.name}</strong>
          ${this.points
            .filter(
              (context: Highcharts.TooltipFormatterContextObject): boolean =>
                context.series.options.custom.categoryNameStd !==
                IndexesAllocation.CASH_NEGATIVE
            )
            .map(
              (context: Highcharts.TooltipFormatterContextObject): string =>
                `
                <br />
                <span style="color:${context.point.color}">&#9632;</span>
                ${context.series.name}:
                ${context.point.options.custom.breakdownPercentage}
                `
            )
            .join('')}
        `;
        },
        shared: true,
      },
      xAxis: {
        endOnTick: true,
        ordinal: false,
        tickmarkPlacement: 'on',
        tickInterval: millisecondInYear,
        type: 'datetime',
        minPadding: 0.0008, // Setting tickInterval raised issue,initial tick started after some gap. Fix
        dateTimeLabelFormats: {
          day: '%b %d',
          week: '%b %d',
          month: "%b '%y",
        },
        events: {
          setExtremes(event) {
            if (event?.trigger === 'navigator') {
              serviceScope.userRangeChange$.next(true);
              /**
               * While setting tickintervals there is no option to set maxTickinterval.
               * so using the logic below to calculate difference in years of the navigator handles between max and min
               * if difference of year is greater than 15 then set tickInterval otherwise let navigator calculate the
               * intervals according to highcharts
               */
              const diff = (event.max - event.min) / millisecondInYear;
              const maxInterval = diff > 15 ? millisecondInYear : null;
              this?.chart?.xAxis[0]?.update({
                tickInterval: maxInterval,
              });
            }
          },
        },
      },
      yAxis: {
        labels: {
          format: '{value}%',
          y: 5,
        },
        opposite: false,
        showLastLabel: true,
        title: {
          text: null,
        },
      },
    };
    //  set y-axis max from config, or default to 150%
    (opts.yAxis as Highcharts.YAxisOptions).max =
      this.brConfig.allocationsYMax || this.brConfig.allocationsYMax === 0
        ? this.brConfig.allocationsYMax
        : 150;

    //  set y-axis  min from config, or default to -50%
    (opts.yAxis as Highcharts.YAxisOptions).min =
      this.brConfig.allocationsYMin || this.brConfig.allocationsYMin === 0
        ? this.brConfig.allocationsYMin
        : -50;

    //  set y-axis tick interval from config, or default to 25%
    (opts.yAxis as Highcharts.YAxisOptions).tickInterval =
      this.brConfig.allocationsYTickInterval ||
      this.brConfig.allocationsYTickInterval === 0
        ? this.brConfig.allocationsYTickInterval
        : 25;
    return opts;
  };

  private mapChartConfig = (indexesData: Indexes): ChartConfig => {
    logger.debug('Indexes Data', indexesData);
    const series: Highcharts.SeriesAreaOptions[] = Object.values(
      groupBy<IndexAssetAllocation>(
        indexesData.indexAssetAllocations,
        this.groupSeries
      )
    )
      .reduce<Highcharts.SeriesAreaOptions[]>(this.reduceSeries, [])
      .sort(this.sortSeries);

    logger.debug('series', series);

    const legendSeries: LegendSeries[] = series
      .filter(
        (seriesOpts: Highcharts.SeriesAreaOptions): boolean =>
          seriesOpts.custom.categoryNameStd !== IndexesAllocation.CASH_NEGATIVE
      )
      .map(
        (seriesOpts: Highcharts.SeriesAreaOptions): LegendSeries => ({
          seriesName: seriesOpts.name,
          categoryNameStd: seriesOpts.custom.categoryNameStd,
          color: seriesOpts.color as string,
        })
      );
    if (this.brConfig.allocationsLegendOrder) {
      legendSeries.sort(this.sortLegendSeries);
    }

    logger.debug('legendSeries', legendSeries);

    const dataLength: number = series[0]?.data.length;
    const asOfDate: string = dataLength
      ? ((series[0].data[dataLength - 1] as Highcharts.PointOptionsObject)
          .name as string)
      : null;

    const config: ChartConfig = {
      chartTitle: 'products.us-index-allocations-chart-title',
      proximalPlacementTop: 'IndexAllocationsProximalTop',
      proximalPlacementBottom: 'IndexAllocationsProximalBottom',
      caveatPlacement: 'IndexAllocations',
      tooltip: this.translateService.tooltip(
        'products.us-index-allocations-chart-tooltip'
      ),
      asOfDate,
      highChartOptions: this.mapChartOptions(series),
      dateRangeEnum: INDEXES_ALLOCATIONS_DATE_RANGES,
      selectedToggleRange: '',
      customRange: null,
      legendSeries,
    };

    return config;
  };

  public replaceWithAmpersand(data: string): string {
    const regex = /&amp;/gi;
    return data.replace(regex, '&');
  }

  public downloadExcel() {
    forkJoin([
      this.indexes$,
      this.getProximalCaveat$('AllocationWeightsExcelBottom'),
      this.getProximalCaveat$('AllocationWeightsExcelTop'),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([data, bottomCaveat, topCaveat]) => {
        // take the allocations data and group by category into 2D array
        this.categories = Object.values<IndexAssetAllocation[]>(
          groupBy<IndexAssetAllocation>(
            data.indexAssetAllocations,
            this.groupSeries
          )
        ).sort(this.sortCategories);
        // map data into suitable format for xls
        this.cellData = this.mapExcel(this.categories);
        import('xlsx').then((xlsx) => {
          const merges = [];
          const maxColMerge = 200; // *** Change here for number of columns to be merged for caveats. ***

          // Step 1: add top caveat line,
          const workSheet = xlsx.utils.aoa_to_sheet(topCaveat, {
            cellStyles: true,
          });

          // Step 2: For each top caveat line, merge it to single cell and add to 'merges' array.
          topCaveat.forEach((caveat, index) => {
            merges.push({
              s: {
                r: index,
                c: 0,
              },
              e: {
                r: index,
                c: maxColMerge,
              },
            });
          });

          // Step 3: Add cellData.
          xlsx.utils.sheet_add_aoa(workSheet, this.cellData, {
            origin: 'A' + (topCaveat.length + 3),
          });

          // Step 4: Calculate bottom caveat start row.
          const bottomCaveatRowStart =
            topCaveat.length + this.cellData.length + 5;

          // Step 5: Add bottom caveats starting from above calculated start row.
          xlsx.utils.sheet_add_aoa(workSheet, bottomCaveat, {
            origin: 'A' + bottomCaveatRowStart,
          });

          // Step 6: For each bottom caveat, again, merge it to single cell and add to 'merges' array.
          bottomCaveat.forEach((caveat, index) => {
            merges.push({
              s: {
                r: bottomCaveatRowStart + index,
                c: 0,
              },
              e: {
                r: bottomCaveatRowStart + index,
                c: maxColMerge,
              },
            });
          });

          // Step 7: Finally, configure the merges & column width and append sheet to the workbook.
          workSheet['!merges'] = merges;

          // work out column widths based on number of chars in category names
          const charWidth = this.categories.reduce(
            (w, r) => Math.max(w, r[0].categoryDescription.length),
            10
          );

          const wscols = [
            { wpx: 130 },
            { wch: charWidth },
            { wch: charWidth },
            { wch: charWidth },
            { wpx: 140 },
          ];
          workSheet['!cols'] = wscols;

          const workBook = xlsx.utils.book_new();

          xlsx.utils.book_append_sheet(workBook, workSheet, this.ticker);

          xlsx.writeFile(
            workBook,
            `${this.fundName} ${this.labels?.allocation_weights_header_download_excel}.${this.labels['us-index-download-file-extension']}`
          );
        });
      });
  }
  private mapExcel(categories: IndexAssetAllocation[][]): string[][] {
    logger.debug('categories', categories);
    // create headings row
    const headings: string[] = categories.map(this.mapHeading);
    // add as-of date heading to start of row
    headings.unshift(this.translateService.instant('products.as-of'));

    const rows: string[][] = categories[0].map(this.mapRow(categories));
    return [headings].concat(rows);
  }

  private mapHeading = (category: IndexAssetAllocation[]): string =>
    category[0].categoryDescription;

  private mapBreakdown = (index: number) => (
    category: IndexAssetAllocation[]
  ): string => category[index].breakdownPercentage + '%';

  private mapRow = (categories: IndexAssetAllocation[][]) => (
    category: IndexAssetAllocation,
    index: number
  ): string[] => {
    const rowData = categories.map(this.mapBreakdown(index));
    // add as-of date to first col
    rowData.unshift(category.asOfDate);
    return rowData;
  };
  /**
   * get the proximal caveats for a particular placement
   */
  private getProximalCaveat$(placement: string): Observable<string[][]> {
    return this.caveatService.getProximals$(placement).pipe(
      take(1),
      map((caveatContext) => {
        const caveat = [];
        const caveatData = caveatContext.map((data) => data.split('\n'));

        caveatData[0]?.forEach((data: any) => {
          let trimedData = data.replace(/(<([^>]+)>)/gi, '');
          trimedData = this.replaceWithAmpersand(trimedData);

          if (trimedData.length > 0) {
            caveat.push([trimedData]);
          }
        });

        return caveat;
      }),
      catchError(() => {
        return [];
      })
    );
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
